import React, { useState } from "react";
import { Checkbox, Stack, Divider, Chip } from "@mui/material";
import { Icon } from "@iconify/react";

const PermissionListing = ({ menuData, setMenuData }) => {
  const [expanded, setExpanded] = useState(null);

  // ------- helpers -------
  const cascadeSection = (section, newState) => ({
    ...section,
    state: newState,
    subMenu: (section.subMenu || []).map(s => ({ ...s, state: newState })),
  });

  const cascadeModule = (module, newState) => ({
    ...module,
    state: newState,
    section: (module.section || []).map(sec => cascadeSection(sec, newState)),
  });

  const fromChildrenState = (children) => {
    if (!children || children.length === 0) return false;
    const all = children.every(c => c.state === true);
    return all ? true : false;
  };

  const recalcSection = (section) => {
    const sub = section.subMenu || [];
    return {
      ...section,
      state: fromChildrenState(sub),
    };
  };

  const recalcModule = (module) => {
    const sections = (module.section || []).map(recalcSection);
    return {
      ...module,
      section: sections,
      state: fromChildrenState(sections),
    };
  };

  // ------- main updater (preserves your signature & call sites) -------
  const updateAtPath = (items, id, newState, actionType, path) => {
    // Module-level "Check All" (only the module being viewed)
    if (actionType === "check_all" && typeof id === "number") {
      const updatedModule = recalcModule(cascadeModule(menuData[id], newState));
      return menuData.map((m, i) => (i === id ? updatedModule : m));
    }

    // For nested toggles we use path: [moduleIdx, sectionIdx] or [moduleIdx, sectionIdx, subIdx]
    if (!Array.isArray(path) || path.length === 0) return menuData;

    const [modIdx, secIdx, leafIdx] = path;

    // Section-level toggle
    if (path.length === 2) {
      const mod = menuData[modIdx];
      const section = mod.section[secIdx];

      const updatedSection = recalcSection(cascadeSection(section, newState));
      const updatedModule = recalcModule({
        ...mod,
        section: mod.section.map((s, j) => (j === secIdx ? updatedSection : s)),
      });

      return menuData.map((m, i) => (i === modIdx ? updatedModule : m));
    }

    // Leaf-level toggle
    if (path.length === 3) {
      const mod = menuData[modIdx];
      const section = mod.section[secIdx];

      const updatedSubMenu = section.subMenu.map((s, k) =>
        k === leafIdx ? { ...s, state: newState } : s
      );
      const updatedSection = recalcSection({ ...section, subMenu: updatedSubMenu });
      const updatedModule = recalcModule({
        ...mod,
        section: mod.section.map((s, j) => (j === secIdx ? updatedSection : s)),
      });

      return menuData.map((m, i) => (i === modIdx ? updatedModule : m));
    }

    // Default (no-op)
    return menuData;
  };

  // ------- recursive render for sections -> subMenu -------
  const renderItems = (items, path = []) =>
    (items || []).map((item, index) => {
      const currentPath = [...path, index];
      const permis = item?.permissionName || [];
      return (
        <div key={currentPath.join("-")}>
          <Stack direction="row" pl={path.length * 2.5} alignItems="center">
            <Checkbox
              checked={item.state === true}
              indeterminate={item.state === "indeterminate"}
              sx={{
                "&.Mui-checked": { color: "#a0a0a0" },
                "& .MuiSvgIcon-root": { fontSize: 18 },
              }}
              onChange={(e) =>
                setMenuData(updateAtPath([item], index, e.target.checked, null, currentPath))
              }
            />
            {item.title} {permis.includes("mobile") && "(Mobile)"}
          </Stack>

          {item.subMenu && renderItems(item.subMenu, currentPath)}
        </div>
      );
    });

  return (
    <>
      {menuData?.length > 0 &&
        menuData.map((x, idx) => (
          <div key={idx} className="pt-3">
            <Stack direction="row" alignItems="center">
              <Icon
                onClick={() => setExpanded(expanded === idx ? null : idx)}
                style={{ cursor: "pointer" }}
                icon={
                  expanded === idx
                    ? "fluent:chevron-up-12-filled"
                    : "fluent:chevron-down-12-filled"
                }
                width="12"
              />
              <span className="text-bold px-3">{x?.title}</span>
            </Stack>

            {expanded === idx && (
              <Stack>
                {/* Module "Check All" */}
                <Stack direction="row" pt={1} pl={2.5} alignItems="center">
                  <Checkbox
                    checked={x.state === true}
                    indeterminate={x.state === "indeterminate"}
                    sx={{
                      "&.Mui-checked": { color: "#a0a0a0" },
                      "& .MuiSvgIcon-root": { fontSize: 18 },
                    }}
                    onChange={(e) =>
                      setMenuData(updateAtPath([x], idx, e.target.checked, "check_all", null))
                    }
                  />
                  Check All
                </Stack>

                {/* Sections + leaves */}
                <Stack direction="column">{renderItems(x.section || [], [idx])}</Stack>
              </Stack>
            )}

            <Divider className="pt-2" />
          </div>
        ))}
    </>
  );
};

export default PermissionListing;
